/* import __COLOCATED_TEMPLATE__ from './list.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import CustomBotListData from 'embercom/objects/operator/configuration/custom-bots/custom-bot-list-data';
import { inject as service } from '@ember/service';
import {
  matchBehaviors,
  objectTypes,
} from 'embercom/models/data/matching-system/matching-constants';
import {
  AI_AGENT_WORKFLOW_TARGET,
  AI_AGENT_WORKFLOW_TRIGGER,
  CHANNEL_ZENDESK_TICKET,
  CHANNEL_ZENDESK_SUNSHINE_CONVERSATION,
  CHANNEL_SALESFORCE_CASE,
  EVERYONE_PREDICATE,
  TRIGGERABLE_BOT_TYPE,
  CHANNEL_WEB,
  CHANNEL_ALL,
  CUSTOM_TASK_TEMPLATES,
} from 'embercom/lib/operator/custom-bots/constants';
import { taskFor } from 'ember-concurrency-ts';
import Ruleset from 'embercom/models/matching-system/ruleset';
import { action } from '@ember/object';
import type Store from '@ember-data/store';
import type RouterService from '@ember/routing/router-service';
import { ajaxDelete } from 'embercom/lib/ajax';
import { postRequest } from 'embercom/lib/inbox/requests';
import { states } from 'embercom/models/data/matching-system/matching-constants';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import type IntlService from 'ember-intl/services/intl';
import { tracked } from '@glimmer/tracking';
import type Template from 'embercom/models/template';
import { task } from 'ember-concurrency-decorators';
import { type BotConfigTarget } from 'embercom/objects/operator/configuration/configuration';
import type BannerDismissalService from 'embercom/services/banner-dismissal-service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type ModalService from 'embercom/services/modal-service';

const STANDALONE_COPILOT_BANNER = 'standalone-tasks-list-not-available-in-copilot-banner';
const TASK_PAGE_SIZE = 100;

export interface Args {
  title: string | null;
  state: string | null;
}

export default class StandaloneTasksList extends Component<Args> {
  listData: CustomBotListData;

  @tracked showContentCreationModal = false;
  @tracked templates: Template[] = [];
  @tracked selectedTemplate: Template | null = null;
  @tracked selectedTemplateContentObject: any = null;
  @tracked objectTypeOverride: number | null = null;
  @tracked selectedTarget: BotConfigTarget | null = null;
  @tracked disableTransitionBack = false;
  @tracked private hasDismissedStandaloneCopilotBanner = true;
  @tracked showAllTasks = false;
  @tracked showAllProcedures = false;

  @service declare appService: AppService;
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare intercomEventService: $TSFixMe;
  @service declare outboundHomeService: any;
  @service declare bannerDismissalService: BannerDismissalService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare modalService: ModalService;
  @service declare notificationsService: $TSFixMe;
  @service declare permissionsService: $TSFixMe;

  states = states;

  constructor(owner: any, args: Args) {
    super(owner, args);
    this.listData = new CustomBotListData(
      owner,
      this.appService.app.id,
      AI_AGENT_WORKFLOW_TARGET,
      objectTypes.triggerableCustomBot,
    );

    taskFor(this.loadTemplates).perform();
    this.computeStandaloneCopilotBannerVisibility();
  }

  willDestroy() {
    super.willDestroy();
    this.outboundHomeService.resetFilters();
  }

  get isLoadingInitialData() {
    return taskFor(this.listData.setupInitialState).isRunning;
  }

  get procedures(): any[] {
    if (this.appService.app.canUseFinProcedures) {
      return (
        (this.listData.bots ?? []).filter((bot: any) => {
          let firstContent = bot.contents?.get('firstObject');
          let contentData = firstContent?.contentData;

          if (contentData?.is_procedure !== true) {
            return false;
          }

          if (this.appService.app.canUseFinVoiceProceduresInUI) {
            return true;
          }

          let target_channels: Array<string> = contentData.target_channels;

          return !target_channels?.includes('phone_call');
        }) ?? []
      );
    }

    return [];
  }

  get visibleProcedures(): any[] {
    if (this.showAllProcedures || this.procedures.length <= 10) {
      return this.procedures;
    }
    return this.procedures.slice(0, 10);
  }

  get tasks(): any[] {
    return (
      (this.listData.bots ?? []).filter((bot: any) => {
        let firstContent = bot.contents?.get('firstObject');
        let contentData = firstContent?.contentData;
        return contentData?.is_procedure !== true;
      }) ?? []
    );
  }

  get visibleTasks(): any[] {
    if (this.showAllTasks || this.tasks.length <= 3) {
      return this.tasks;
    }
    return this.tasks.slice(0, 3);
  }

  @action
  toggleShowAllTasks() {
    this.showAllTasks = !this.showAllTasks;
  }

  @action
  toggleShowAllProcedures() {
    this.showAllProcedures = !this.showAllProcedures;
  }

  get app() {
    return this.appService.app;
  }

  get startFromScratchTemplate() {
    return {
      title: 'start-from-scratch',
      icon: 'new',
      id: 'start-from-scratch',
    } as unknown as Template;
  }

  private async computeStandaloneCopilotBannerVisibility() {
    this.hasDismissedStandaloneCopilotBanner =
      await this.bannerDismissalService.hasDismissed(STANDALONE_COPILOT_BANNER);
  }

  get canSeeStandaloneCopilotBanner() {
    return (
      this.appService.app.canUseCopilotOnStandalone &&
      this.finStandaloneService.platform.hasInstalledCopilot &&
      !this.hasDismissedStandaloneCopilotBanner
    );
  }

  get canCreateAndEditProcedures() {
    return this.permissionsService.currentAdminCan('can_create_and_edit_bots');
  }

  get groupList() {
    return [
      {
        items: [
          {
            text: this.intl.t('standalone.tasks.list.let-ai-draft-procedure'),
            onSelectItem: () => this.newProcedureFromAI(),
            icon: 'ai-stars',
          },
          {
            text: this.intl.t('standalone.tasks.list.create-from-scratch'),
            onSelectItem: () => this.newProcedureFromScratch(),
            icon: 'new',
          },
        ],
      },
    ];
  }

  @action
  hideStandaloneCopilotBanner() {
    this.hasDismissedStandaloneCopilotBanner = true;
    this.bannerDismissalService.dismiss(STANDALONE_COPILOT_BANNER);
  }

  @task({ drop: true })
  *loadTemplates() {
    this.templates = yield this.store.findAll('template');
  }

  get taskTemplates() {
    return this.templates.filter((template) => template.objective === CUSTOM_TASK_TEMPLATES.id);
  }

  @action
  selectTemplate(template: Template, isFromEmptyState = false) {
    this.disableTransitionBack = isFromEmptyState;
    this.selectedTemplateContentObject = template.ruleset.rulesetLinks.firstObject.object;
    this.selectedTarget = this.selectedTemplateContentObject.target;
    this.selectedTemplate = template;
  }

  @action
  newTask() {
    this.app.canUseFinTaskTemplate ? this.toggleTemplateModal() : this.createTask();
  }

  @action
  async newProcedureFromScratch() {
    if (!this.canCreateAndEditProcedures) {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_create_and_edit_bots',
      );
      return;
    }
    await this.createProcedure();
  }

  private async createProcedure() {
    try {
      let response = await postRequest(`/ember/procedures?app_id=${this.appService.app.id}`);
      let data = await response.json();

      if (data?.id) {
        // Navigate to teammate-app procedure page, with preview open if combined module is enabled
        let route = this.appService.app.canUseFeature(
          'team-actions-fin-procedures-dedicated-page-combined-testing-module',
        )
          ? 'apps.app.automation.fin-ai-agent.procedures.procedure.preview'
          : 'apps.app.automation.fin-ai-agent.procedures.procedure';
        this.router.transitionTo(route, data.id);
      }
    } catch (error) {
      // notifyResponseError automatically handles 403 errors with required_permission
      // by showing the permission modal via permissionsService.checkPermission()
      this.notificationsService.notifyResponseError(error);
    }
  }

  @action
  async newProcedureFromAI() {
    if (!this.canCreateAndEditProcedures) {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_create_and_edit_bots',
      );
      return;
    }
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'ai_procedure_generation_start',
      place: 'procedures',
      section: 'fin_ai_agent',
    });
    interface ProcedureGenerationResult {
      procedureRulesetId: string;
      procedureId: string;
    }

    this.modalService.openModal('react/workflows/fin-tasks-ai-generated-procedure-modal', {
      isOpen: true,
      onClose: async (result?: string) => {
        if (result === 'close') {
          // User clicked Close - just close modal, no action
          this.modalService.closeModal();
          return;
        } else if (result) {
          let response = JSON.parse(result) as ProcedureGenerationResult;

          let route = this.appService.app.canUseFeature(
            'team-actions-fin-procedures-dedicated-page-combined-testing-module',
          )
            ? 'apps.app.automation.fin-ai-agent.procedures.procedure.preview'
            : 'apps.app.automation.fin-ai-agent.procedures.procedure';
          this.router.transitionTo(route, response.procedureId, {
            queryParams: { mode: 'edit' },
          });
        }
        this.modalService.closeModal();
      },
    });
  }

  @action
  askAnExpert() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'ask_an_expert_button',
      place: 'standalone_tasks',
    });
  }

  @action
  toggleTemplateModal(selectedTemplateId?: string) {
    this.showContentCreationModal = !this.showContentCreationModal;
    if (!this.showContentCreationModal) {
      this.objectTypeOverride = null;
      this.selectedTemplate = null;
      this.selectedTarget = null;
    } else if (selectedTemplateId) {
      let template = this.templates.find((template) => template.id === selectedTemplateId);
      if (template) {
        this.selectTemplate(template, true);
      }
    }
  }

  @action
  createTaskFromTemplate(
    contentType: string | null = null,
    _deliveryChannel = null,
    _audienceType = null,
    templateId: string | null = null,
  ) {
    this.showContentCreationModal = false;
    let rulesetParams: {
      app_id: string;
      object_type: string | null;
      template_id?: string;
      role_predicate_group?: object;
      object_data?: object;
      match_behavior?: number;
    } = {
      app_id: this.app.id,
      object_type: contentType,
    };

    if (templateId) {
      rulesetParams.template_id = templateId;
    } else {
      rulesetParams.match_behavior = matchBehaviors.transient;
      rulesetParams.role_predicate_group = { predicates: [EVERYONE_PREDICATE] };

      rulesetParams.object_data = {
        target_channels: [CHANNEL_ALL],
        type: TRIGGERABLE_BOT_TYPE,
        trigger_type: AI_AGENT_WORKFLOW_TRIGGER,
      };
    }

    this.router.transitionTo('apps.app.content.new', rulesetParams);
  }

  @action async createTask() {
    let targetChannels = [CHANNEL_WEB];

    try {
      if (this.appService.app.isStandaloneApp) {
        if (this.appService.app.hasStandalonePlatform('zendesk')) {
          targetChannels.pushObjects([
            CHANNEL_ZENDESK_SUNSHINE_CONVERSATION,
            CHANNEL_ZENDESK_TICKET,
          ]);
        }

        if (this.appService.app.hasStandalonePlatform('salesforce')) {
          targetChannels.pushObjects([CHANNEL_SALESFORCE_CASE]);
        }

        let ruleset = await Ruleset.createForType(this.store, {
          app_id: this.appService.app.id,
          match_behavior: matchBehaviors.transient,
          role_predicate_group: { predicates: [EVERYONE_PREDICATE] },
          object_type: objectTypes.triggerableCustomBot,
          object_data: {
            target_channels: targetChannels,
            trigger_type: AI_AGENT_WORKFLOW_TRIGGER,
            type: TRIGGERABLE_BOT_TYPE,
            bot_data: {
              is_procedure: false,
            },
          },
        });
        this.intercomEventService.trackAnalyticsEvent({
          action: 'created',
          object: 'task',
          place: 'standalone_tasks',
        });
        this.router.transitionTo('apps.app.standalone.tasks.workflow', ruleset.id, {
          queryParams: { mode: 'edit' },
        });
      } else {
        targetChannels = [CHANNEL_ALL];

        let ruleset = await Ruleset.createForType(this.store, {
          app_id: this.appService.app.id,
          match_behavior: matchBehaviors.transient,
          role_predicate_group: { predicates: [EVERYONE_PREDICATE] },
          object_type: objectTypes.triggerableCustomBot,
          object_data: {
            target_channels: targetChannels,
            trigger_type: AI_AGENT_WORKFLOW_TRIGGER,
            type: TRIGGERABLE_BOT_TYPE,
            bot_data: {
              is_procedure: false,
            },
          },
        });
        this.intercomEventService.trackAnalyticsEvent({
          action: 'created',
          object: 'task',
          place: 'fin_tasks',
        });
        this.router.transitionTo('apps.app.automation.fin-ai-agent.tasks.task', ruleset.id, {
          queryParams: { mode: 'edit' },
        });
      }
    } catch (error) {
      // notifyResponseError automatically handles 403 errors with required_permission
      // by showing the permission modal via permissionsService.checkPermission()
      this.notificationsService.notifyResponseError(error);
    }
  }

  @action async deleteTaskOrProcess(contentWrapper: { contentWrapperId: string; title: string }) {
    let confirmed = await this.intercomConfirmService.confirm({
      title: this.intl.t('standalone.tasks.list.your-processes.delete-modal.title', {
        processTitle:
          contentWrapper.title ||
          this.intl.t('standalone.tasks.list.your-processes.untitled-process'),
      }),
      body: this.intl.t('standalone.tasks.list.your-processes.delete-modal.body'),
      primaryButtonType: 'primary-destructive',
      confirmButtonText: this.intl.t('standalone.tasks.list.your-processes.delete-modal.delete'),
      cancelButtonText: this.intl.t('standalone.tasks.list.your-processes.delete-modal.cancel'),
    });

    if (!confirmed) {
      return;
    }

    await ajaxDelete(`/ember/matching_system/rulesets/${contentWrapper.contentWrapperId}`, {
      app_id: this.appService.app.id,
      admin_id: this.appService.app.currentAdmin.id,
    });

    this.listData.bots = this.listData.bots.filter(
      (bot) => bot.id !== contentWrapper.contentWrapperId,
    );

    taskFor(this.listData.reload).perform(TASK_PAGE_SIZE);
  }

  @action
  showMessenger() {
    window.Intercom('showNewMessage');
  }

  @task({ restartable: true })
  *fetchAllTasks() {
    this._setFilterValues();
    this.listData.title = this.args.title;
    yield taskFor(this.listData.setupInitialState).perform(TASK_PAGE_SIZE);

    // Auto-load all remaining pages if there are more items
    while (this.listData.bots.length < this.listData.totalCount) {
      let prevLength = this.listData.bots.length;
      yield taskFor(this.listData.fetchNextPage).perform();
      if (this.listData.bots.length === prevLength) {
        break;
      }
    }
  }

  @action
  filterContent(_filterProperty: string, value: any) {
    let queryParams = { state: value };
    this.router.transitionTo('apps.app.automation.fin-ai-agent.tasks.index', {
      queryParams,
    });
  }

  @action
  resetSearch() {
    this.performSearchTransition({ name: '' });
  }

  @action
  performSearchTransition(item?: { name: string }) {
    let queryParams = {
      title: item && item.name.length > 0 ? item.name : null,
    };
    this.router.transitionTo('apps.app.automation.fin-ai-agent.tasks.index', { queryParams });
  }

  @action
  resetFilters() {
    this.router.transitionTo('apps.app.automation.fin-ai-agent.tasks.index', {
      queryParams: {
        title: null,
        state: null,
      },
    });
  }

  _setFilterValues() {
    this.outboundHomeService.safelySet(
      'selectedStateValue',
      this.args.state !== null ? parseInt(this.args.state, 10) : null,
    );
  }

  get hasAppliedFilters() {
    return !!(this.args.title !== null || this.selectedStateValue !== null);
  }

  get selectedStateValue() {
    return this.outboundHomeService.selectedStateValue;
  }

  @action
  viewTask(taskId: string) {
    if (this.app.canUseFeature('workflow-draft-state')) {
      this.router.transitionTo('apps.app.automation.fin-ai-agent.tasks.task', taskId, {
        queryParams: { mode: 'view' },
      });
    } else {
      this.router.transitionTo('apps.app.automation.fin-ai-agent.tasks.task', taskId);
    }
  }

  @action
  viewTaskForStandalone(taskId: string) {
    if (this.app.canUseFeature('workflow-draft-state')) {
      this.router.transitionTo('apps.app.standalone.tasks.workflow', taskId, {
        queryParams: { mode: 'view' },
      });
    } else {
      this.router.transitionTo('apps.app.standalone.tasks.workflow', taskId);
    }
  }

  @action
  viewProcedure(contentId: string) {
    let route = this.appService.app.canUseFeature(
      'team-actions-fin-procedures-dedicated-page-combined-testing-module',
    )
      ? 'apps.app.automation.fin-ai-agent.procedures.procedure.preview'
      : 'apps.app.automation.fin-ai-agent.procedures.procedure';
    this.router.transitionTo(route, contentId);
  }

  @action
  viewProcedureForStandalone(contentId: string) {
    this.router.transitionTo('apps.app.standalone.procedures.procedure.preview', contentId);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Tasks::List': typeof StandaloneTasksList;
    'standalone/tasks/list': typeof StandaloneTasksList;
  }
}
